import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { trackShareModalViewed } from '../events'
import useFetchShareLinks from '../hooks/useFetchShareLinks'
import i18nInstance from '../i18n/i18nInstance'
import ShareContext, { ShareContextData } from '../provider/ShareContext'
import { ShareModal } from './ShareModal'

const { Provider } = ShareContext

interface ShareModalConnectedProps {
  show: boolean
  onClose: () => void
  shareData: ShareContextData
  modalTitle?: string
  useDeprecated?: boolean
}

const ShareModalConnected = ({
  show,
  onClose,
  shareData,
  modalTitle,
  useDeprecated = false,
}: ShareModalConnectedProps) => {
  const { links, loading, error, onFetchLinks } = useFetchShareLinks(shareData)
  const { t } = useTranslation('@mc/share')

  const [isNativeShare] = useState(
    () =>
      !!(
        typeof window !== 'undefined' &&
        window.navigator &&
        window.navigator.share
      ),
  )

  useEffect(() => {
    const { course, chapter, source, article } = shareData
    const isLessonShare = !!chapter?.slug

    if (show) {
      const properties = {
        ...(chapter && { lesson: chapter.slug }),
        page: course ? course.slug : article?.slug,
        source,
      }
      trackShareModalViewed(properties)

      onFetchLinks({ useDeprecated, isLessonShare })
    }
  }, [show, shareData, useDeprecated, onFetchLinks])

  const email = useMemo(
    () => ({
      subject: shareData.title,
      body: t('email.body', {
        instructorName: shareData.course?.instructorName,
        shareLink: links?.email,
      }),
    }),
    [links, shareData, t],
  )

  return (
    <Provider value={shareData}>
      <ShareModal
        show={show}
        isNativeShare={isNativeShare}
        links={links}
        loading={loading}
        error={error}
        onClose={onClose}
        modalTitle={modalTitle}
        modalImage={shareData.modalImage}
        email={email}
      />
    </Provider>
  )
}

export default withI18nContext(i18nInstance, { ns: '@mc/share' })(
  ShareModalConnected,
)
